/* eslint-disable no-unused-vars -- Address/parcel UI commented out; state and handlers kept for re-enable */
import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { API_BASE, apiGet, apiGetOwnershipXml } from './api/client';
import { useAuth } from './context/AuthContext';

/** Extract object ID and type from property-details or parcel response for LV-by-object API. */
function getObjectIdAndType(data) {
  if (!data || typeof data !== 'object') return null;
  const item = (Array.isArray(data.data) && data.data[0]) ? data.data[0] : data;
  const id =
    item.jednotkaId ?? item.jednotka?.id ?? item.parcelaId ?? item.id ?? item.stavbaId ??
    item.parcela?.id ?? item.stavba?.id;
  if (id == null) return null;
  const numId = Number(id);
  if (!Number.isInteger(numId) || numId < 1) return null;
  const typ = item.typParcely || item.parcelaId != null || item.parcela?.id != null ? 'PAR_S' :
    item.stavbaId != null || item.stavba?.id != null ? 'STA_S' : 'JED_S';
  return { id: numId, typ };
}

/** LV number + Kod KU from lookup result (parcel/building with lv). Prefer this for LV report. */
function getLVInfo(data) {
  if (!data || typeof data !== 'object') return null;
  const item = (Array.isArray(data.data) && data.data[0]) ? data.data[0] : data;
  const lv = item.lv;
  const cislo = lv?.cislo ?? lv?.cisloLv;
  const kod = lv?.katastralniUzemi?.kod ?? item?.katastralniUzemi?.kod;
  if (cislo == null || kod == null) return null;
  return { KodKU: String(kod), CisloLv: String(cislo) };
}

export default function IdentifyPropertyPage({ onBack }) {
  const { t } = useTranslation();
  const { getToken } = useAuth();

  // Single "Obec a část obce" typeahead – serves both address and parcel paths
  const [addressInput, setAddressInput] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressLoading, setAddressLoading] = useState(false);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [selectedObec, setSelectedObec] = useState(null);
  const [selectedCast, setSelectedCast] = useState(null);
  const addressDebounceRef = useRef(null);
  const addressWrapperRef = useRef(null);

  const [form, setForm] = useState({
    TypStavby: 'SCislemPopisnym',
    NazevUlice: '',
    CisloDomovni: '',
    CisloJednotky: '',
    KodKU: '',
    CisloLv: '',
    CisloPar: '',
    DruhCislovaniParcely: '2',
  });

  const [kuList, setKuList] = useState([]);
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const [parcelOnlyResult, setParcelOnlyResult] = useState(null);
  const [parcelOnlyLoading, setParcelOnlyLoading] = useState(false);

  const [ownershipXmlLoading, setOwnershipXmlLoading] = useState(false);
  const [ownershipXmlResult, setOwnershipXmlResult] = useState(null);
  const [ownershipXmlError, setOwnershipXmlError] = useState(null);

  const [unitsList, setUnitsList] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);

  const effectiveKodKU = form.KodKU.trim()
    || (kuList[0]?.kod != null ? String(kuList[0].kod) : '')
    || (selectedCast?.kodKU != null ? String(selectedCast.kodKU) : '');

  // Debounced address suggestions (Obec + Část obce) – min 3 chars
  useEffect(() => {
    const q = addressInput.trim();
    if (q.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    addressDebounceRef.current = setTimeout(() => {
      setAddressLoading(true);
      fetch(`${API_BASE}/api/address-suggestions?q=${encodeURIComponent(q)}`)
        .then((res) => res.json())
        .then((data) => {
          setAddressSuggestions(data.suggestions || []);
          setShowAddressSuggestions(true);
        })
        .catch(() => setAddressSuggestions([]))
        .finally(() => setAddressLoading(false));
    }, 250);
    return () => {
      if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    };
  }, [addressInput]);

  // Close address dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target)) {
        setShowAddressSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectAddressSuggestion = (suggestion) => {
    setSelectedObec(suggestion.obec || null);
    setSelectedCast(suggestion.cast || null);
    setAddressInput(suggestion.label);
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  const handleClearPlace = () => {
    setAddressInput('');
    setSelectedObec(null);
    setSelectedCast(null);
    setKuList([]);
    setUnitsList([]);
    setForm((prev) => (prev.CisloJednotky ? { ...prev, CisloJednotky: '' } : prev));
  };

  // Fetch KU when municipality selected (for parcel path and effectiveKodKU)
  useEffect(() => {
    if (!selectedObec) {
      setKuList([]);
      return;
    }
    fetch(`${API_BASE}/api/katastralni-uzemi?kodObce=${selectedObec.kod}`)
      .then((res) => res.json())
      .then((data) => setKuList(Array.isArray(data) ? data : (data?.data ?? [])))
      .catch(() => setKuList([]));
  }, [selectedObec]);

  // Load units (jednotky) when address info filled
  useEffect(() => {
    const kodCastiObce = selectedCast?.kod;
    const typStavby = form.TypStavby;
    const cisloDomovni = (form.CisloDomovni || '').trim();
    if (!kodCastiObce || !typStavby || cisloDomovni === '') {
      setUnitsList([]);
      setForm(prev =>
        prev.CisloJednotky ? { ...prev, CisloJednotky: '' } : prev,
      );
      return;
    }

    let cancelled = false;
    setUnitsLoading(true);
    setUnitsList([]);

    const params = new URLSearchParams({
      KodCastiObce: kodCastiObce,
      TypStavby: typStavby,
      CisloDomovni: cisloDomovni,
    });
    const nazevUlice = (form.NazevUlice || '').trim();
    if (nazevUlice !== '') params.set('NazevUlice', nazevUlice);

    fetch(`${API_BASE}/api/units?${params}`)
      .then(res => res.json())
      .then(data => {
        if (cancelled) return;
        const arr = Array.isArray(data?.data) ? data.data : [];
        setUnitsList(arr);
        setForm(prev => {
          if (!prev.CisloJednotky || prev.CisloJednotky.trim() === '') return prev;
          const inList = arr.some(
            u => String(u.cisloJednotky) === String(prev.CisloJednotky),
          );
          return inList ? prev : { ...prev, CisloJednotky: '' };
        });
      })
      .catch(() => {
        if (!cancelled) setUnitsList([]);
      })
      .finally(() => {
        if (!cancelled) setUnitsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedCast?.kod, form.TypStavby, form.CisloDomovni, form.NazevUlice]);

  const handleLookup = async (e) => {
    e.preventDefault();
    // Address path requires part of municipality (cast) + house number
    if (!selectedCast) {
      alert(t('form.fillRequired') + ' ' + t('identify.placeObecCastHint'));
      return;
    }
    if (!form.CisloDomovni?.trim()) {
      alert(t('form.fillRequired'));
      return;
    }

    const queryParams = {
      KodCastiObce: selectedCast.kod,
      TypStavby: form.TypStavby,
      CisloDomovni: form.CisloDomovni,
    };
    const nazevUlice = (form.NazevUlice || '').trim();
    if (nazevUlice !== '') queryParams.NazevUlice = nazevUlice;
    const unitNum = form.CisloJednotky.trim();
    if (unitNum !== '' && unitNum !== '0') {
      queryParams.CisloJednotky = unitNum;
    }

    setLookupLoading(true);
    setLookupResult(null);
    setOwnershipXmlResult(null);
    setOwnershipXmlError(null);

    try {
      const res = await fetch(
        `${API_BASE}/api/property-details?${new URLSearchParams(queryParams)}`,
      );
      const data = await res.json();
      setLookupResult(data);
    } catch (err) {
      console.error(err);
      alert(t('form.fetchFailed'));
    } finally {
      setLookupLoading(false);
    }
  };

  const handleParcelOnlyLookup = async (e) => {
    e.preventDefault();
    const kodKU = effectiveKodKU;
    const cisloPar = form.CisloPar.trim().replace(/\s/g, '');
    if (!kodKU || !cisloPar) {
      alert(selectedObec ? t('parcel.enterParcel') : t('parcel.selectObecFirst'));
      return;
    }

    setParcelOnlyLoading(true);
    setParcelOnlyResult(null);
    setLookupResult(null);

    const q = new URLSearchParams({ KodKU: kodKU, CisloPar: cisloPar });
    try {
      const res = await fetch(`${API_BASE}/api/parcel-details?${q}`);
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        setParcelOnlyResult({
          error: t('errors.serverNonJson'),
          status: res.status,
          preview: text.slice(0, 150) + '…',
        });
        return;
      }
      setParcelOnlyResult(data);
      if (data?.data?.length) setLookupResult(data);
    } catch (err) {
      setParcelOnlyResult({ error: err.message });
    } finally {
      setParcelOnlyLoading(false);
    }
  };

  const objectInfo = lookupResult ? getObjectIdAndType(lookupResult) : null;
  const lvInfo = lookupResult ? getLVInfo(lookupResult) : null;

  const handleGetOwnershipXml = async () => {
    if (!objectInfo && !lvInfo) return;
    setOwnershipXmlLoading(true);
    setOwnershipXmlError(null);
    setOwnershipXmlResult(null);
    try {
      let path;
      if (lvInfo) {
        const { res, data } = await apiGetOwnershipXml({
          KodKU: lvInfo.KodKU,
          CisloLv: lvInfo.CisloLv,
          getToken,
          confirmMessage: t('lvWorkspace.refreshWarning'),
          allowPaidFetch: true,
        });
        if (!res.ok) throw new Error(data.error || 'Failed to fetch LV report');
        setOwnershipXmlResult(data);
        return;
      } else {
        path = `/api/ownership-xml-by-object?typ=${encodeURIComponent(
          objectInfo.typ,
        )}&id=${objectInfo.id}`;
      }
      const res = await apiGet(path, getToken);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to fetch LV report');
      setOwnershipXmlResult(data);
    } catch (err) {
      setOwnershipXmlError(err.message);
    } finally {
      setOwnershipXmlLoading(false);
    }
  };

  /** Get full LV report (XML) by Kod KU + Číslo LV. */
  const handleGetLvByKuAndLv = async (e) => {
    e.preventDefault();
    const kodKU = (form.KodKU || '').trim();
    const cisloLv = (form.CisloLv || '').trim();
    if (!kodKU || !cisloLv) {
      alert(t('form.fillRequired'));
      return;
    }
    setOwnershipXmlLoading(true);
    setOwnershipXmlError(null);
    setOwnershipXmlResult(null);
    try {
      const { res, data } = await apiGetOwnershipXml({
        KodKU: kodKU,
        CisloLv: cisloLv,
        getToken,
        confirmMessage: t('lvWorkspace.refreshWarning'),
        allowPaidFetch: true,
      });
      if (!res.ok) throw new Error(data.error || 'Failed to fetch LV report');
      setOwnershipXmlResult(data);
    } catch (err) {
      setOwnershipXmlError(err.message);
    } finally {
      setOwnershipXmlLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto py-4 px-3 sm:px-4 space-y-6 w-full min-w-0">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-blue-600 hover:underline"
        >
          ← {t('unitSearch.back')}
        </button>
      )}

      <p className="text-gray-700">{t('identify.goal')}</p>
      <p className="text-sm text-gray-600">
        {t('identify.byParcelOnlyHint')}{' '}
        <a
          href="https://nahlizenidokn.cuzk.gov.cz"
          target="_blank"
          rel="noreferrer noopener"
          className="underline"
        >
          nahlizenidokn.cuzk.gov.cz
        </a>
      </p>
      <p className="text-xs text-gray-500 mt-1">
        {t('identify.lvSourceHint')}{' '}
        <a
          href="https://nahlizenidokn.cuzk.gov.cz"
          target="_blank"
          rel="noreferrer noopener"
          className="underline"
        >
          nahlizenidokn.cuzk.gov.cz
        </a>
      </p>

      {/* Option 2 – By Kod KU + Číslo LV: get LV report directly (data from nahlizenidokn.cuzk.gov.cz) */}
      <section className="p-4 border rounded-lg bg-white shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {t('identify.getLvByKuAndLv')}
        </h2>
        <form onSubmit={handleGetLvByKuAndLv} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('identify.kodKULabel')}
            </label>
            <input
              type="text"
              value={form.KodKU}
              onChange={(e) => setForm({ ...form, KodKU: e.target.value })}
              placeholder={t('identify.kodKUPlaceholderShort')}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('identify.cisloLvLabel')}
            </label>
            <input
              type="text"
              value={form.CisloLv}
              onChange={(e) => setForm({ ...form, CisloLv: e.target.value })}
              placeholder={t('identify.cisloLvPlaceholder')}
              className="w-full p-2 border rounded"
            />
          </div>
          <button
            type="submit"
            disabled={ownershipXmlLoading || !form.KodKU?.trim() || !form.CisloLv?.trim()}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {ownershipXmlLoading ? t('result.fetchingLv') : t('identify.getLvByKuAndLv')}
          </button>
        </form>
      </section>

      {/* ========== OPTION 1 – By address (commented out; may need later) ==========
      <section className="p-4 border rounded-lg bg-white shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-3">
          {t('identify.byAddress')}
        </h2>
        <form onSubmit={handleLookup} className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('identify.cisloDomovniShort')}
            </label>
            <input
              type="text"
              value={form.CisloDomovni}
              onChange={(e) =>
                setForm({ ...form, CisloDomovni: e.target.value })
              }
              placeholder={t('identify.cisloDomovniShortPlaceholder')}
              className="w-full p-2 border rounded"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('form.nazevUlice')} <span className="text-gray-500 font-normal">({t('form.optionalShort')})</span>
            </label>
            <input
              type="text"
              value={form.NazevUlice}
              onChange={(e) => setForm({ ...form, NazevUlice: e.target.value })}
              placeholder={t('form.nazevUlicePlaceholder')}
              className="w-full p-2 border rounded"
            />
            <p className="text-xs text-amber-700 mt-1 bg-amber-50 px-2 py-1 rounded">
              {t('form.nazevUliceDisambiguate')}
            </p>
          </div>
          {(selectedCast && form.CisloDomovni?.trim()) && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                {t('form.cisloJednotkyOptional')}
              </label>
              {unitsList.length > 0 ? (
                <select
                  value={form.CisloJednotky}
                  onChange={(e) =>
                    setForm({ ...form, CisloJednotky: e.target.value })
                  }
                  className="w-full p-2 border rounded"
                  disabled={unitsLoading}
                >
                  <option value="">
                    {unitsLoading ? t('form.cisloJednotkyLoading') : t('form.cisloJednotkyWholeBuilding')}
                  </option>
                  {!unitsLoading &&
                    unitsList.map((u) => (
                      <option key={u.id ?? u.cisloJednotky} value={u.cisloJednotky}>
                        {t('form.cisloJednotkyUnitLabel', { number: u.cisloJednotky })}
                      </option>
                    ))}
                </select>
              ) : !unitsLoading && (
                <>
                  <input
                    type="text"
                    value={form.CisloJednotky}
                    onChange={(e) =>
                      setForm({ ...form, CisloJednotky: e.target.value })
                    }
                    placeholder={t('form.cisloJednotkyManualPlaceholder')}
                    className="w-full p-2 border rounded mt-1"
                  />
                  <p className="text-xs text-gray-500 mt-1">{t('form.cisloJednotkyManualHint')}</p>
                </>
              )}
            </div>
          )}
          <button
            type="submit"
            disabled={
              lookupLoading || !selectedCast || !form.CisloDomovni?.trim()
            }
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {lookupLoading ? t('form.lookingUp') : t('identify.findProperty')}
          </button>
        </form>
      </section>
      ========== END OPTION 1 ========== */}

      {/* ========== By parcel (Obec + parcel number) – commented out; may need later ==========
      <section className="p-4 border rounded-lg bg-white shadow-sm">
        <h2 className="text-lg font-semibold text-gray-800 mb-2">{t('identify.byParcel')}</h2>
        <p className="text-sm text-gray-600 mb-3">...</p>
        <form onSubmit={handleParcelOnlyLookup}>...</form>
      </section>
      ========== END By parcel ========== */}

      {/* Result area – LV report (XML) from Kod KU + Číslo LV */}
      {(ownershipXmlResult || ownershipXmlError) && (
        <section className="p-4 border rounded-lg bg-green-50 space-y-3">
          <h3 className="font-semibold text-gray-800">{t('result.lvReportXml')}</h3>
          {ownershipXmlError && (
            <div className="text-red-600 text-sm">{ownershipXmlError}</div>
          )}
          {ownershipXmlResult && (
            <>
              {ownershipXmlResult.xml != null && (
                <div className="space-y-1">
                  <a
                    href={`data:application/xml;charset=utf-8,${encodeURIComponent(ownershipXmlResult.xml)}`}
                    download={`ownership-${form.KodKU || 'ku'}-${form.CisloLv || 'lv'}.xml`}
                    className="inline-block text-blue-600 hover:underline text-sm"
                  >
                    {t('result.downloadXml')}
                  </a>
                  <div className="border rounded p-2 bg-white max-h-96 overflow-auto">
                    <pre className="whitespace-pre-wrap text-xs font-mono">
                      {ownershipXmlResult.xml}
                    </pre>
                  </div>
                </div>
              )}
              {ownershipXmlResult.parsed != null && (
                <details className="border rounded p-2 bg-white">
                  <summary className="cursor-pointer text-sm text-gray-700">{t('result.parsedData')}</summary>
                  <pre className="whitespace-pre-wrap text-xs mt-2 max-h-64 overflow-auto">
                    {JSON.stringify(ownershipXmlResult.parsed, null, 2)}
                  </pre>
                </details>
              )}
              {ownershipXmlResult.xml == null && ownershipXmlResult.parsed == null && (
                <div className="border rounded p-2 bg-white max-h-96 overflow-auto">
                  <pre className="whitespace-pre-wrap text-xs">
                    {JSON.stringify(ownershipXmlResult, null, 2)}
                  </pre>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {/* Legacy result area (when lookupResult from parcel/address – kept for when we re-enable those paths) */}
      {lookupResult && (
        <section className="p-4 border rounded-lg bg-green-50 space-y-3">
          <h3 className="font-semibold text-gray-800">
            {t('result.propertyFound')}
          </h3>
          {(objectInfo || lvInfo) && (
            <div className="flex flex-wrap items-center gap-2">
              {lvInfo && (
                <span className="text-gray-600">
                  {t('result.lvInfo', {
                    cislo: lvInfo.CisloLv,
                    kod: lvInfo.KodKU,
                  })}
                </span>
              )}
              {objectInfo && !lvInfo && (
                <span className="text-gray-600">
                  {t('result.objectId', {
                    id: objectInfo.id,
                    typ: objectInfo.typ,
                  })}
                </span>
              )}
              <button
                type="button"
                onClick={handleGetOwnershipXml}
                disabled={ownershipXmlLoading}
                className="bg-emerald-600 text-white px-3 py-1.5 rounded text-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                {ownershipXmlLoading
                  ? t('result.fetchingLv')
                  : t('result.getLvReportForContract')}
              </button>
            </div>
          )}
          {ownershipXmlError && (
            <div className="text-red-600 text-sm">{ownershipXmlError}</div>
          )}
          {ownershipXmlResult && (
            <div className="border rounded p-2 bg-white max-h-48 overflow-auto">
              <h4 className="font-medium mb-1">{t('result.lvReportXml')}</h4>
              {ownershipXmlResult.xml != null ? (
                <pre className="whitespace-pre-wrap text-xs">{ownershipXmlResult.xml}</pre>
              ) : (
                <pre className="whitespace-pre-wrap text-xs">
                  {JSON.stringify(ownershipXmlResult.parsed ?? ownershipXmlResult, null, 2)}
                </pre>
              )}
            </div>
          )}
          <details className="mt-2">
            <summary className="cursor-pointer text-sm text-gray-600">
              {t('result.title')} (raw)
            </summary>
            <pre className="whitespace-pre-wrap text-xs mt-1 overflow-auto max-h-48">
              {JSON.stringify(lookupResult, null, 2)}
            </pre>
          </details>
        </section>
      )}

      {parcelOnlyResult != null && !lookupResult && (
        <section className="p-4 border rounded bg-amber-50 text-sm">
          <h3 className="font-semibold mb-2">
            {t('result.parcelOnlyResult')}
          </h3>
          <pre className="whitespace-pre-wrap text-xs overflow-auto max-h-64">
            {JSON.stringify(parcelOnlyResult, null, 2)}
          </pre>
        </section>
      )}
    </div>
  );
}

